import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { FileText, ChevronLeft, ChevronRight, BarChart3, Sparkles, Link2, Calendar, Check, X, ArrowRight } from 'lucide-react'
import { api } from '../../lib/api'
import { ScanFilters, FilterState } from '../../components/scans/ScanFilters'
import { ScanGallery } from '../../components/scans/ScanGallery'
import { Scan } from '../../components/scans/ScanCard'

interface ScanHistoryResponse {
  scans: Scan[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

interface ScanStats {
  total_scans: number
  by_subject: Record<string, number>
  by_type: Record<string, number>
  total_xp_from_scans: number
  uncategorized_count: number
  recent_count: number
  potential_xp: number
}

interface ExpectedWork {
  id: number
  canvas_id: string
  title: string
  course_name: string
  course_id: number | null
  start_at: string | null
  event_type: string
  has_scan: boolean
  work_scan_id: number | null
}

interface UnlinkedScan {
  id: number
  subject: string | null
  title: string | null
  scan_type: string | null
  grade_detected: number | null
  created_at: string
  image_path: string | null
}

interface LinkedPair {
  expected_id: number
  scan_id: number
  title: string
}

interface MatchingViewResponse {
  expected_work: ExpectedWork[]
  unlinked_scans: UnlinkedScan[]
  linked_pairs: LinkedPair[]
  stats: {
    total_expected: number
    unlinked_scans: number
    linked: number
  }
}

interface LinkResult {
  success: boolean
  scan_id: number
  calendar_event_id: number
  event_title: string
  xp_awarded: number
}

export default function ScanHistory() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') === 'match' ? 'match' : 'gallery'
  const [activeTab, setActiveTab] = useState<'gallery' | 'match'>(initialTab)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<FilterState>({
    startDate: '',
    endDate: '',
    subject: '',
    scanType: '',
    uncategorized: searchParams.get('uncategorized') === 'true',
  })

  // Match tab state
  const queryClient = useQueryClient()
  const [selectedEvent, setSelectedEvent] = useState<ExpectedWork | null>(null)
  const [selectedScan, setSelectedScan] = useState<UnlinkedScan | null>(null)
  const [days, setDays] = useState(7)

  // Build query params for gallery
  const queryParams = new URLSearchParams()
  queryParams.set('page', page.toString())
  queryParams.set('limit', '20')
  if (filters.startDate) queryParams.set('start_date', filters.startDate)
  if (filters.endDate) queryParams.set('end_date', filters.endDate)
  if (filters.subject) queryParams.set('subject', filters.subject)
  if (filters.scanType) queryParams.set('scan_type', filters.scanType)
  if (filters.uncategorized) queryParams.set('uncategorized', 'true')

  const { data, isLoading } = useQuery<ScanHistoryResponse>({
    queryKey: ['scan-history', page, filters],
    queryFn: async () => {
      const response = await api.get(`/api/student/scans?${queryParams}`)
      return response.data
    },
    enabled: activeTab === 'gallery',
  })

  const { data: stats } = useQuery<ScanStats>({
    queryKey: ['scan-stats'],
    queryFn: async () => {
      const response = await api.get('/api/student/scans/stats')
      return response.data
    },
  })

  // Match tab queries
  const { data: matchData, isLoading: matchLoading } = useQuery<MatchingViewResponse>({
    queryKey: ['matching-view', days],
    queryFn: async () => {
      const response = await api.get(`/api/student/matching-view?days=${days}`)
      return response.data
    },
    enabled: activeTab === 'match',
  })

  const linkMutation = useMutation<LinkResult, Error, { scanId: number; eventId: number }>({
    mutationFn: async ({ scanId, eventId }) => {
      const response = await api.post(`/api/student/scans/${scanId}/link`, {
        calendar_event_id: eventId,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matching-view'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['scan-stats'] })
      setSelectedEvent(null)
      setSelectedScan(null)
    },
  })

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters)
    setPage(1)
  }

  const handleTabChange = (tab: 'gallery' | 'match') => {
    setActiveTab(tab)
    const newParams = new URLSearchParams(searchParams)
    if (tab === 'match') {
      newParams.set('tab', 'match')
    } else {
      newParams.delete('tab')
    }
    setSearchParams(newParams, { replace: true })
  }

  const handleLink = () => {
    if (selectedEvent && selectedScan) {
      linkMutation.mutate({
        scanId: selectedScan.id,
        eventId: selectedEvent.id,
      })
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'No date'
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    })
  }

  const isToday = (dateStr: string | null) => {
    if (!dateStr) return false
    const date = new Date(dateStr)
    const today = new Date()
    return date.toDateString() === today.toDateString()
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-quest-primary/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-quest-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Scans</h1>
            <p className="text-sm text-gray-400">
              {stats ? `${stats.total_scans} total scans` : 'Loading...'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700/50 pb-2">
        <button
          onClick={() => handleTabChange('gallery')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'gallery'
              ? 'bg-quest-primary text-white'
              : 'text-gray-400 hover:bg-surface-hover hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4" />
          All Scans
        </button>
        <button
          onClick={() => handleTabChange('match')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'match'
              ? 'bg-quest-primary text-white'
              : 'text-gray-400 hover:bg-surface-hover hover:text-white'
          }`}
        >
          <Link2 className="w-4 h-4" />
          Match to Assignments
          {matchData && matchData.stats.unlinked_scans > 0 && (
            <span className="ml-1 px-2 py-0.5 text-xs bg-orange-500/20 text-orange-400 rounded-full">
              {matchData.stats.unlinked_scans}
            </span>
          )}
        </button>
      </div>

      {/* Gallery Tab Content */}
      {activeTab === 'gallery' && (
        <>
          {/* Stats summary */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-lg font-bold">{stats.total_scans}</p>
                  <p className="text-xs text-gray-400">Total Scans</p>
                </div>
              </div>

              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-lg font-bold">{stats.recent_count}</p>
                  <p className="text-xs text-gray-400">This Week</p>
                </div>
              </div>

              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-quest-accent/20 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-quest-accent" />
                </div>
                <div>
                  <p className="text-lg font-bold">+{stats.total_xp_from_scans}</p>
                  <p className="text-xs text-gray-400">XP Earned</p>
                </div>
              </div>

              {stats.uncategorized_count > 0 && (
                <button
                  onClick={() => handleFilterChange({ ...filters, uncategorized: true })}
                  className="card flex items-center gap-3 border-orange-500/30 bg-orange-900/10 hover:bg-orange-900/20 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <span className="text-lg">!</span>
                  </div>
                  <div className="text-left">
                    <p className="text-lg font-bold text-orange-400">
                      {stats.uncategorized_count}
                    </p>
                    <p className="text-xs text-orange-300">
                      Need info (+{stats.potential_xp} XP)
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Filters */}
          <ScanFilters
            onFilterChange={handleFilterChange}
            initialFilters={filters}
            showUncategorizedToggle={true}
          />

          {/* Scan gallery */}
          <ScanGallery
            scans={data?.scans || []}
            isLoading={isLoading}
          />

          {/* Pagination */}
          {data && data.pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                Showing {(data.pagination.page - 1) * data.pagination.limit + 1}-
                {Math.min(data.pagination.page * data.pagination.limit, data.pagination.total)} of{' '}
                {data.pagination.total}
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <span className="px-3 py-1 text-sm">
                  {page} / {data.pagination.pages}
                </span>

                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.pages}
                  className="p-2 rounded-lg bg-surface-card hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Match Tab Content */}
      {activeTab === 'match' && (
        <>
          {/* Date range selector */}
          <div className="flex items-center justify-between">
            <p className="text-gray-400">Link your scanned work to expected assignments for bonus XP</p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Show:</span>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="bg-surface-card border border-gray-700/50 rounded-lg px-3 py-1 text-sm"
              >
                <option value={3}>Last 3 days</option>
                <option value={7}>Last week</option>
                <option value={14}>Last 2 weeks</option>
                <option value={30}>Last month</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          {matchData && (
            <div className="grid grid-cols-3 gap-4">
              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-lg font-bold">{matchData.stats.total_expected}</p>
                  <p className="text-xs text-gray-400">Expected Work</p>
                </div>
              </div>

              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-lg font-bold">{matchData.stats.unlinked_scans}</p>
                  <p className="text-xs text-gray-400">Unlinked Scans</p>
                </div>
              </div>

              <div className="card flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-lg font-bold">{matchData.stats.linked}</p>
                  <p className="text-xs text-gray-400">Matched</p>
                </div>
              </div>
            </div>
          )}

          {/* Success toast */}
          {linkMutation.isSuccess && (
            <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-green-400" />
                <span>
                  Matched! <span className="text-quest-accent font-bold">+{linkMutation.data?.xp_awarded} XP</span>
                </span>
              </div>
              <Sparkles className="w-5 h-5 text-quest-accent" />
            </div>
          )}

          {/* Link action bar */}
          {(selectedEvent || selectedScan) && (
            <div className="bg-quest-primary/10 border border-quest-primary/30 rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Selected event */}
                  <div className={`px-4 py-2 rounded-lg ${selectedEvent ? 'bg-blue-500/20 border border-blue-500/30' : 'bg-surface-card border border-gray-700/50 border-dashed'}`}>
                    {selectedEvent ? (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-400" />
                        <span className="text-sm font-medium truncate max-w-[200px]">{selectedEvent.title}</span>
                        <button
                          onClick={() => setSelectedEvent(null)}
                          className="p-1 rounded hover:bg-surface-hover"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Select expected work</span>
                    )}
                  </div>

                  <ArrowRight className="w-5 h-5 text-gray-500" />

                  {/* Selected scan */}
                  <div className={`px-4 py-2 rounded-lg ${selectedScan ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-surface-card border border-gray-700/50 border-dashed'}`}>
                    {selectedScan ? (
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-orange-400" />
                        <span className="text-sm font-medium truncate max-w-[200px]">
                          {selectedScan.title || selectedScan.subject || 'Untitled Scan'}
                        </span>
                        <button
                          onClick={() => setSelectedScan(null)}
                          className="p-1 rounded hover:bg-surface-hover"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Select a scan</span>
                    )}
                  </div>
                </div>

                {/* Link button */}
                <button
                  onClick={handleLink}
                  disabled={!selectedEvent || !selectedScan || linkMutation.isPending}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Link2 className="w-4 h-4" />
                  {linkMutation.isPending ? 'Linking...' : 'Link (+10 XP)'}
                </button>
              </div>
            </div>
          )}

          {/* Two column view */}
          {matchLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-quest-primary border-t-transparent" />
            </div>
          ) : matchData ? (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Expected Work Column */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-400" />
                  Expected Work
                </h2>

                {matchData.expected_work.length === 0 ? (
                  <div className="card text-center py-8 text-gray-400">
                    <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>All work is matched!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {matchData.expected_work.map((event) => (
                      <button
                        key={event.id}
                        onClick={() => setSelectedEvent(selectedEvent?.id === event.id ? null : event)}
                        className={`w-full text-left card p-3 transition-all ${
                          selectedEvent?.id === event.id
                            ? 'border-blue-500 bg-blue-500/10'
                            : 'hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">{event.title}</h3>
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                              <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                                {event.course_name}
                              </span>
                              <span className={isToday(event.start_at) ? 'text-quest-accent' : ''}>
                                {formatDate(event.start_at)}
                              </span>
                            </div>
                          </div>
                          {selectedEvent?.id === event.id && (
                            <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                              <Check className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Scans Column */}
              <div className="space-y-4">
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-orange-400" />
                  Your Scans (unlinked)
                </h2>

                {matchData.unlinked_scans.length === 0 ? (
                  <div className="card text-center py-8 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No unlinked scans</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {matchData.unlinked_scans.map((scan) => (
                      <button
                        key={scan.id}
                        onClick={() => setSelectedScan(selectedScan?.id === scan.id ? null : scan)}
                        className={`w-full text-left card p-3 transition-all ${
                          selectedScan?.id === scan.id
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium truncate">
                              {scan.title || scan.subject || 'Untitled Scan'}
                            </h3>
                            <div className="flex items-center gap-2 mt-1 text-sm text-gray-400">
                              {scan.subject && (
                                <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                                  {scan.subject}
                                </span>
                              )}
                              {scan.scan_type && (
                                <span className="px-2 py-0.5 rounded bg-gray-700 text-xs">
                                  {scan.scan_type}
                                </span>
                              )}
                              <span>{formatDate(scan.created_at)}</span>
                              {scan.grade_detected !== null && (
                                <span className="text-quest-accent">
                                  {scan.grade_detected}%
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedScan?.id === scan.id && (
                            <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center">
                              <Check className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}

          {/* Linked pairs */}
          {matchData && matchData.linked_pairs.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                Already Matched ({matchData.linked_pairs.length})
              </h2>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {matchData.linked_pairs.map((pair) => (
                  <div
                    key={pair.expected_id}
                    className="card p-3 bg-green-900/10 border-green-500/30"
                  >
                    <div className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="truncate text-sm">{pair.title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
